import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ApiService from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { DocsMarkdown } from './DocsMarkdown';
import { getPrevNext, type DocContent } from './docsData';

const contentModules = import.meta.glob<{ default: DocContent }>('./content/*.ts', { eager: true });

function getContent(slug: string): DocContent | null {
  const key = `./content/${slug}.ts`;
  const mod = contentModules[key];
  return mod?.default ?? null;
}

/**
 * Placeholders a doc can use to show the reader their OWN details.
 *
 * The docs are public, so most readers won't be signed in — every placeholder
 * needs a fallback that still reads as a sensible instruction. Substitution is
 * generic rather than MCP-specific so any page can use it.
 */
const PLACEHOLDERS = ['{{CONNECTOR_URL}}', '{{ACCOUNT_ID}}', '{{ACCOUNT_NAME}}', '{{INSTALL_BUTTON}}'];

type AccountDetails = {
  account_id: string;
  account_name: string;
  connector_url: string;
  install_url?: string;
};

function substitute(content: string, account: AccountDetails | null): string {
  const fallback: AccountDetails = {
    account_id: 'YOUR-ACCOUNT-ID',
    account_name: 'your account',
    // Origin rather than a hardcoded host: customers on a custom domain need
    // to see their own.
    connector_url: `${window.location.origin}/mcp/t/YOUR-ACCOUNT-ID`,
  };
  const values = account ?? fallback;

  // The install button is the one placeholder with no sensible anonymous
  // fallback. A link built from a placeholder account would add a connector
  // pointing at an account that doesn't exist — worse than no button, because
  // it fails later, inside Claude, where nothing explains why. Signed-out
  // readers get the manual steps below it instead.
  const installButton = account?.install_url
    ? `[Add to Claude](${account.install_url})`
    : account
      // Signed in, but the account came back without a link. Telling them to
      // sign in would be nonsense — point at the manual steps instead.
      ? '_Use the manual steps below to add the connector._'
      : '_Sign in to Pliable and this page will give you a one-click install link for your account._';

  return content
    .replaceAll('{{CONNECTOR_URL}}', values.connector_url)
    .replaceAll('{{ACCOUNT_ID}}', values.account_id)
    .replaceAll('{{ACCOUNT_NAME}}', values.account_name)
    .replaceAll('{{INSTALL_BUTTON}}', installButton);
}

export default function DocPage() {
  const { slug } = useParams<{ slug: string }>();
  const doc = slug ? getContent(slug) : null;
  const nav = slug ? getPrevNext(slug) : { prev: undefined, next: undefined };

  const [account, setAccount] = useState<AccountDetails | null>(null);
  const needsAccount = !!doc && PLACEHOLDERS.some((p) => doc.content.includes(p));

  // An anonymous reader — the common case on public docs — never makes the call
  // at all, so the fallbacks apply with no 401 noise.
  //
  // Gate on what ApiService will actually send, not on the auth context. Those
  // two disagree on a cold load — the context has a user before the token
  // reaches the singleton ApiService reads — which is why this 401'd on direct
  // navigation but worked when reached from inside the SPA.
  // Gate on what ApiService will actually send, not on the auth context. The two
  // disagree on a cold load — the context has a user before the token reaches
  // the singleton ApiService reads — which is why this 401'd on direct
  // navigation and worked when reached from inside the SPA.
  const apiReady = useApiAuthReady();

  useEffect(() => {
    // Only for pages that actually use a placeholder — no point calling the API
    // on every docs page.
    if (!needsAccount || !apiReady) return;
    ApiService.getInstance()
      .request('GET', '/mcp/connector-info')
      .then((data: any) => {
        if (data?.connector_url) setAccount(data as AccountDetails);
      })
      .catch(() => {});
  }, [needsAccount, apiReady, slug]);

  const content = useMemo(
    () => (doc ? substitute(doc.content, account) : ''),
    [doc, account]
  );

  if (!doc) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
        <p className="text-muted-foreground">
          The documentation page you're looking for doesn't exist.
        </p>
        <Link to="/docs/connecting-to-snowflake" className="text-primary underline mt-4 inline-block">
          Go to docs home
        </Link>
      </div>
    );
  }

  return (
    <article>
      <h1 className="text-3xl font-bold tracking-tight mb-8">{doc.title}</h1>
      <DocsMarkdown content={content} />

      {/* Prev / Next navigation */}
      <div className="flex items-center justify-between mt-12 pt-6 border-t border-border">
        {nav.prev ? (
          <Link
            to={`/docs/${nav.prev.slug}`}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {nav.prev.title}
          </Link>
        ) : <div />}
        {nav.next ? (
          <Link
            to={`/docs/${nav.next.slug}`}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {nav.next.title}
            <ChevronRight className="h-4 w-4" />
          </Link>
        ) : <div />}
      </div>
    </article>
  );
}
